import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  listSignals,
  getSignal,
  confirmSignal,
  dismissSignal,
  convertSignalToReminder,
  convertSignalToEntity,
  bulkSignalAction,
} from '../api/client';
import {
  SignalCard,
  FilterBar,
  EvidencePanel,
  EmptyState,
} from '../components';
import type { Signal } from '../components/SignalCard';
import type { FilterConfig } from '../components/FilterBar';
import type { EvidenceMessage } from '../components/EvidencePanel';

interface SignalDetail {
  evidence_messages: EvidenceMessage[];
  explanation: string;
}

export default function Signals() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const [signals, setSignals] = useState<Signal[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  // Filters
  const initialFilters: Record<string, string> = {
    category: searchParams.get('category') ?? '',
    urgency: searchParams.get('urgency') ?? '',
    status: searchParams.get('status') ?? '',
    source: searchParams.get('source') ?? '',
  };
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>(initialFilters);

  // Expanded signal
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [expandedDetail, setExpandedDetail] = useState<SignalDetail | null>(null);
  const [expandLoading, setExpandLoading] = useState(false);

  // Selection for bulk actions
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const filterConfigs: FilterConfig[] = [
    {
      key: 'category',
      label: t('common.filter') + ': ' + 'Category',
      options: [
        'customer_order', 'order_change', 'delivery_update', 'pickup_request',
        'supplier_issue', 'inventory_shortage', 'production_task',
        'roasting_request', 'packaging_request', 'invoice_or_payment',
        'followup_needed', 'reminder', 'urgent_issue', 'general_information', 'noise',
      ].map((c) => ({ value: c, label: t(`categories.${c}`) })),
    },
    {
      key: 'urgency',
      label: t('common.filter') + ': ' + 'Urgency',
      options: ['critical', 'high', 'medium', 'low'].map((u) => ({
        value: u,
        label: t(`urgency.${u}`),
      })),
    },
    {
      key: 'status',
      label: t('common.filter') + ': ' + 'Status',
      options: [
        'new', 'awaiting_review', 'confirmed', 'reminder_created',
        'in_progress', 'resolved', 'dismissed', 'merged',
      ].map((s) => ({ value: s, label: t(`status.${s}`) })),
    },
  ];

  const fetchSignals = useCallback(
    async (pageNum: number, append = false) => {
      if (!append) setLoading(true);
      else setLoadingMore(true);

      try {
        const params: Record<string, string | number> = { limit: 20, page: pageNum };
        Object.entries(activeFilters).forEach(([k, v]) => {
          if (v) params[k] = v;
        });

        const res = await listSignals(params);
        const data = res.data;
        const items: Signal[] = Array.isArray(data) ? data : data.items ?? [];
        const total: number = data.total ?? items.length;

        if (append) {
          setSignals((prev) => [...prev, ...items]);
        } else {
          setSignals(items);
        }
        setTotalCount(total);
        setHasMore(items.length === 20);
      } catch {
        if (!append) setSignals([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [activeFilters],
  );

  useEffect(() => {
    setPage(1);
    setSelectedIds(new Set());
    fetchSignals(1);
  }, [fetchSignals]);

  const handleFilterChange = (key: string, value: string) => {
    setActiveFilters((prev) => ({ ...prev, [key]: value }));
  };
  const handleClearFilters = () => {
    setActiveFilters({ category: '', urgency: '', status: '', source: '' });
  };

  const handleLoadMore = () => {
    const next = page + 1;
    setPage(next);
    fetchSignals(next, true);
  };

  // Signal actions
  const handleConfirm = async (id: string) => {
    try {
      await confirmSignal(id);
      fetchSignals(1);
    } catch { /* ignore */ }
  };

  const handleDismiss = async (id: string) => {
    try {
      await dismissSignal(id);
      fetchSignals(1);
    } catch { /* ignore */ }
  };

  const handleExpand = async (id: string) => {
    if (expandedId === id) {
      setExpandedId(null);
      setExpandedDetail(null);
      return;
    }
    setExpandedId(id);
    setExpandedDetail(null);
    setExpandLoading(true);
    try {
      const res = await getSignal(id);
      setExpandedDetail({
        evidence_messages: res.data.evidence_messages ?? [],
        explanation: res.data.explanation ?? '',
      });
    } catch {
      setExpandedDetail({ evidence_messages: [], explanation: '' });
    } finally {
      setExpandLoading(false);
    }
  };

  const handleConvertToReminder = async (id: string) => {
    try {
      await convertSignalToReminder(id);
      fetchSignals(1);
    } catch { /* ignore */ }
  };

  const handleConvertToEntity = async (id: string) => {
    try {
      await convertSignalToEntity(id);
      fetchSignals(1);
    } catch { /* ignore */ }
  };

  // Selection
  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === signals.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(signals.map((s) => s.id)));
    }
  };

  const handleBulkAction = async (action: 'confirm' | 'dismiss') => {
    if (selectedIds.size === 0) return;
    try {
      await bulkSignalAction({ action, signal_ids: Array.from(selectedIds) });
      setSelectedIds(new Set());
      fetchSignals(1);
    } catch { /* ignore */ }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        <span className="ml-3 text-gray-500">{t('common.loading')}</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900">{t('signals.title')}</h1>
          {totalCount > 0 && (
            <span className="inline-flex items-center rounded-full bg-primary-100 text-primary-700 px-2.5 py-0.5 text-sm font-medium">
              {totalCount}
            </span>
          )}
        </div>
        <p className="text-sm text-gray-500 mt-1">{t('signals.subtitle')}</p>
      </div>

      {/* Filters */}
      <FilterBar
        filters={filterConfigs}
        activeFilters={activeFilters}
        onFilterChange={handleFilterChange}
        onClear={handleClearFilters}
      />

      {/* Bulk actions toolbar */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-3 rounded-lg border border-primary-200 bg-primary-50 px-4 py-3">
          <span className="text-sm font-medium text-primary-700">
            {selectedIds.size} selected
          </span>
          <button
            onClick={() => handleBulkAction('confirm')}
            className="inline-flex items-center gap-1 rounded-md bg-green-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-green-700 transition-colors"
          >
            ✓ {t('common.confirm')}
          </button>
          <button
            onClick={() => handleBulkAction('dismiss')}
            className="inline-flex items-center gap-1 rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 transition-colors"
          >
            ✗ {t('common.dismiss')}
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="ml-auto text-xs text-gray-500 hover:text-gray-700"
          >
            {t('common.cancel')}
          </button>
        </div>
      )}

      {/* Select all checkbox */}
      {signals.length > 0 && (
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={selectedIds.size === signals.length && signals.length > 0}
              onChange={toggleSelectAll}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            Select all
          </label>
        </div>
      )}

      {/* Signal list */}
      {signals.length === 0 ? (
        <EmptyState
          icon="📡"
          title={t('signals.title')}
          description={t('signals.emptyState')}
        />
      ) : (
        <div className="space-y-3">
          {signals.map((signal) => (
            <div key={signal.id}>
              <div className="flex items-start gap-3">
                {/* Selection checkbox */}
                <div className="pt-4 shrink-0">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(signal.id)}
                    onChange={() => toggleSelect(signal.id)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                </div>

                {/* Signal card + actions */}
                <div className="flex-1 space-y-2">
                  <SignalCard
                    signal={signal}
                    onConfirm={handleConfirm}
                    onDismiss={handleDismiss}
                    onExpand={handleExpand}
                  />

                  {/* Extra action buttons */}
                  <div className="flex items-center gap-2 pl-1">
                    <button
                      onClick={() => handleConvertToReminder(signal.id)}
                      className="inline-flex items-center gap-1 rounded-md bg-purple-50 px-2.5 py-1 text-xs font-medium text-purple-700 hover:bg-purple-100 transition-colors"
                    >
                      🔔 {t('signals.convertToReminder')}
                    </button>
                    <button
                      onClick={() => handleConvertToEntity(signal.id)}
                      className="inline-flex items-center gap-1 rounded-md bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100 transition-colors"
                    >
                      📦 {t('signals.convertToEntity')}
                    </button>
                  </div>

                  {/* Evidence panel (expanded) */}
                  {expandedId === signal.id && (
                    <div className="mt-2">
                      {expandLoading ? (
                        <div className="flex items-center gap-2 py-4 px-3 text-sm text-gray-500">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600" />
                          {t('common.loading')}
                        </div>
                      ) : (
                        expandedDetail && (
                          <EvidencePanel
                            messages={expandedDetail.evidence_messages}
                            explanation={expandedDetail.explanation}
                            isOpen
                            onToggle={() => setExpandedId(null)}
                          />
                        )
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Load more */}
      {hasMore && (
        <div className="flex justify-center pt-4">
          <button
            onClick={handleLoadMore}
            disabled={loadingMore}
            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-6 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            {loadingMore ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2" />
                {t('common.loading')}
              </>
            ) : (
              'Load more'
            )}
          </button>
        </div>
      )}
    </div>
  );
}
